import React from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { Button, Col, Row } from 'antd';

import { ComponentWithStore, withStore } from 'models/RootStore';
import { Story } from 'models/story/Story';
import { Homepage as HomepageModel } from 'models/homepage/Homepage';
import { SubscriptionState } from 'models/subscription/SubscriptionState';

import DefaultLayout from 'layouts/DefaultLayout';
import Homepage from 'components/public/Homepage';
import Catalogue from 'components/public/pages/home/Catalogue';
import LoadingIcon from 'components/public/pages/webgl_app/LoadingIcon';
import StoryMultiPanel from 'components/public/pages/home/StoryMultiPanel';
import StoryTitleImage from 'components/public/pages/webgl_app/StoryTitleImage';
import StoryDetailsButton from "components/public/pages/webgl_app/StoryDetailsButton";

import './Home.scss';
import PlayIcon from "components/shared/icons/PlayIcon";
import BannerStory from "components/shared/banners/BannerStory";

interface IHomeProps {}

interface IHomeState {
    loaded: boolean;
    loggedIn: boolean;
}

// This renders either itself or Logged out Homepage.
class Home extends ComponentWithStore<IHomeProps, IHomeState> {
    @observable private stories: Story[];
    @observable private releasedStories: Story[];
    @observable private comingSoonStories: Story[];
    @observable private featuredStories: Story[];
    @observable private homepage: HomepageModel;

    constructor(props) {
        super(props);

        this.state = {
            loaded: false,
            loggedIn: true
        };

        this.setStories = this.setStories.bind(this);
    }

    private getFeaturedStoryByIndex(index: number): Story | null {
        if (!this.featuredStories || this.featuredStories.length <= index) return null;
        return this.featuredStories[index];
    }

    public signOut(): void {
        this.store.SessionProvider.signOut().then(success => {
            window.location.href = '/sign_in';
        });
    }

    public playClicked(): void {
        const splashStory = this.homepage?.featured_story;
        window.location.href = `/story/${splashStory.id}/play`;
    }

    public componentDidMount(): void {
        if (this.store.SessionProvider.hasCredentials()) {
            this.store.SessionProvider.hasValidCredentials().then(result => {
                if (!result) {
                    if (this.store.SessionProvider.hasCredentials()) {
                        this.store.SessionProvider.clear();
                    }
                    window.location.href = '/sign_in';
                } else {
                    this.store.SubscriptionProvider.subscriptionState().then(result => {
                        if (result !== SubscriptionState.VALID) {
                            window.location.href = '/register';
                        } else {
                            return new HomepageModel(this.store.StoryProvider)
                                .fetchData()
                                .then(homepage => this.homepage = homepage)
                                .then(() => this.store.StoryProvider.getAll())
                                .then((stories: Story[]) => {
                                    this.setStories(stories);
                                    this.setState({ loaded: true });
                                });
                        }
                    });
                }
            });
        } else {
            this.setState({
                loggedIn: false
            });
        }
    }

    private setStories(stories: Story[]): void {
        this.stories = stories;

        this.comingSoonStories = this.stories.filter(story => story.coming_soon);
        this.releasedStories = this.stories.filter(story => !story.coming_soon);

        this.featuredStories = this.releasedStories.filter(story => {
           return story.id !== this.homepage?.featured_story?.id
        });
    }

    public render(): React.ReactElement {
        if (!this.store.SessionProvider.hasCredentials()) {
            if (!this.state.loggedIn) {
                return <Homepage />;
            }
        }
        const { loaded } = this.state;
        if (!loaded) {
            return (
                <DefaultLayout>
                    <div className='rk-home-layout'>{this.renderLoading()}</div>
                </DefaultLayout>
            );
        }

        return (
            <DefaultLayout>
                <div className='rk-home-layout'>
                    {this.renderSplashStory()}
                    {this.renderCatalogueRow()}
                    {this.renderFeaturedRow(0)}
                    {this.renderMultiPanelRow()}
                    {this.renderFeaturedRow(1)}
                </div>
            </DefaultLayout>
        );
    }

    private renderSplashStory() {
        const splashStory = this.homepage?.featured_story;
        const localisation = splashStory?.default_localisation;
        if (!splashStory || !localisation) return <React.Fragment />;

        const landscapeImg = localisation.featured_landscape_image_url || localisation.featured_portrait_image_url;
        const portraitImg = localisation.featured_portrait_image_url || localisation.featured_landscape_image_url;

        return (
            <Row className='rk-home--banner'>
                <Col className={'rk-home--banner__col'} md={24} lg={{ span: 20, offset: 2 }}>
                    <div className='rk-center'>
                        <img className='rk-home--banner-img landscape' width='100%' src={landscapeImg} />
                        <img className='rk-home--banner-img portrait' width='100%' src={portraitImg} />
                        <div className='rk-home--banner-text'>
                            <StoryTitleImage story={splashStory} />
                            <div className={"rk-home--banner-btn"}>
                                <StoryDetailsButton label='Play' icon={<PlayIcon />} color='blue' cursor='pointer' onClick={e => this.playClicked()} />
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
        );
    }

    private renderLoading(): React.ReactElement {
        return (
            <div className='rk-catalogue-row-slider' style={{ width: '100%', height: '100%' }}>
                <LoadingIcon color='blue' />
            </div>
        );
    }

    private renderCatalogueRow() {
        const { loaded } = this.state;
        if (!loaded) return this.renderLoading();

        return (
            <div className='rk-home--catalogue'>
                <Catalogue title='New Releases' showMessage={true} stories={this.releasedStories} homepage={true} />
                <Catalogue title='Coming Soon' showMessage={true} stories={this.comingSoonStories} homepage={true} />
            </div>
        );
    }

    private renderFeaturedRow(index: number) {
        const story = this.getFeaturedStoryByIndex(index);
        if (!story) return this.renderLoading();

        return (
            <Row align='middle' className='rk-home--featured'>
                <Col xs={24} md={{ span: 20, offset: 2 }} className='rk-home--featured-content'>
                    <BannerStory story={story} />
                </Col>
            </Row>
        );
    }

    private renderMultiPanelRow() {
        const { loaded } = this.state;

        if (!loaded) return this.renderLoading();
        return (
            <Row align='middle' className='rk-home--multi-panel'>
                <StoryMultiPanel story={this.homepage?.multi_panel_story} />
            </Row>
        );
    }
}

export default observer(Home);
